// Die Zahlen, die morgens zählen — einmal geholt, von zwei Oberflächen genutzt.
//
// Am Schreibtisch zeigt sie die Übersicht als Kacheln mit Verläufen, in der
// App der Zahlenstreifen über dem Kachelbrett. Zwölf Kacheln, die sich ihre
// Zahl jeweils selbst holen, wären auf dem Telefon zwölf Abfragen beim Start.
import { useEffect, useState } from 'react';
import { api } from '../../../gemeinsam/lib/api';
import type { Anwesenheit, Artikel, Aufgabe, Buchhaltung, Rechnung } from '../../../gemeinsam/lib/typen';

export type Kennzahlen = {
  aufgaben: Aufgabe[];
  team: Anwesenheit[];
  artikel: Artikel[];
  rechnungen: Rechnung[];
  buch: Buchhaltung | null;
  laedt: boolean;
  /** Alle Zweige kamen leer zurück — dann ist eher das Netz weg als der Betrieb. */
  fehler: boolean;
};

export function useKennzahlen(istAdmin: boolean): Kennzahlen {
  const [aufgaben, setAufgaben] = useState<Aufgabe[] | null>(null);
  const [team, setTeam] = useState<Anwesenheit[]>([]);
  const [artikel, setArtikel] = useState<Artikel[]>([]);
  const [rechnungen, setRechnungen] = useState<Rechnung[]>([]);
  const [buch, setBuch] = useState<Buchhaltung | null>(null);
  const [fehler, setFehler] = useState(false);

  useEffect(() => {
    void (async () => {
      // Alles parallel und jeder Zweig für sich abgesichert: fehlt ein Modul im
      // Tarif, soll das Dashboard trotzdem alles andere zeigen.
      let gescheitert = 0;
      const zaehlen = <T,> (ersatz: T) => (): T => { gescheitert++; return ersatz; };
      const [a, p, ar, re, bu] = await Promise.all([
        api<Aufgabe[]>('/tasks').catch(zaehlen([] as Aufgabe[])),
        api<Anwesenheit[]>('/presence/users').catch(zaehlen([] as Anwesenheit[])),
        api<{ produkte: Artikel[] }>('/products').catch(zaehlen({ produkte: [] as Artikel[] })),
        api<{ rechnungen: Rechnung[] }>('/invoices').catch(zaehlen({ rechnungen: [] as Rechnung[] })),
        // Umsatz und Rohertrag sind Chefsache — Mitarbeiter fragen gar nicht erst.
        istAdmin ? api<Buchhaltung>('/accounting/uebersicht').catch(zaehlen(null as Buchhaltung | null))
          : Promise.resolve(null),
      ]);
      setAufgaben(a);
      setTeam(p);
      setArtikel(ar.produkte ?? []);
      setRechnungen(re.rechnungen ?? []);
      setBuch(bu);
      // Vier von vier daneben heißt: kein Netz. Ein einzelner Fehlschlag ist
      // dagegen Alltag — etwa ein Modul, das der Tarif nicht enthält.
      setFehler(gescheitert >= 4);
    })();
  }, [istAdmin]);

  return {
    aufgaben: aufgaben ?? [],
    team, artikel, rechnungen, buch,
    laedt: aufgaben === null,
    fehler,
  };
}
